import { searchTypes } from 'constants/common';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileTypes } from 'types/baseTypes';
import { isPermissionDenied } from 'utils/apiUtils';
import { useAppSelector } from '../../../hooks/typedHooks';
import { useLazyGetReceiptsQuery } from '../api';
import { Receipt } from '../receipt.model';
import { ReceiptStatus } from '../receipts.model';

export interface ReceiptQueryParams {
  status: ReceiptStatus[];
  tipAmountNE: number;
  merchantId: string[];
  customerId: string[];
  type: FileTypes;
  // TODO change to enum
  paymentMethod: string[];
  start: string;
  end: string;
  search: string;
  sourceType: string[];
}

export const initialQueryParamsState = {
  status: [],
  tipAmountNE: null,
  merchantId: [],
  customerId: [],
  paymentMethod: [],
  start: null,
  end: null,
  search: null,
  sourceType: [],
  type: null
};

export const usePaginatedGetReceiptsQuery = () => {
  const [list, setList] = useState<Receipt[]>([]);
  const [queryParams, setQueryParams] = useState<ReceiptQueryParams>(initialQueryParamsState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const page = useRef(0);
  const [getReceiptsData, receiptsState] = useLazyGetReceiptsQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [searchType, setSearchType] = useState(searchTypes[1]);

  const isInitListCall: boolean = page.current === 0;

  useEffect(() => {
    if (!receiptsState?.isFetching) {
      setHasPermissionsToView(true);
      const receipts = receiptsState?.data?.transactions ?? [];
      const updatedList = isInitListCall ? receipts : [...list, ...receipts];
      const receiptsLeftToFetch = receiptsState?.data?.totalCount
        ? +receiptsState?.data?.totalCount - updatedList.length
        : 0;
      if (
        receiptsLeftToFetch > 0 &&
        (!receiptsState?.data?.transactions || receiptsState?.data?.transactions?.length === 0)
      ) {
        setHasMore(false);
      } else {
        setHasMore(receiptsLeftToFetch > 0);
      }

      setList(updatedList);
      if (page.current === 1) {
        setTotalListCount(receiptsState?.data?.totalCount ?? 0);
      }
      setIsLoading(false);
    }
  }, [receiptsState?.isFetching]);

  useEffect(() => {
    if (!receiptsState?.isFetching && receiptsState?.isError) {
      setIsLoading(false);
      setHasMore(false);
      if (isPermissionDenied(receiptsState.error?.['data']?.code, receiptsState.error?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
    }
  }, [receiptsState?.isError]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    loadMore();
  };

  useEffect(() => {
    resetListAndLoadMore();
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    const isSearchPresent = queryParams.search !== null && queryParams.search !== '';
    if (currentTeam?.id) {
      try {
        getReceiptsData({
          teamId: currentTeam?.id,
          limit: 10,
          page: page.current,
          ...queryParams,
          search: null,
          [searchType.type]: isSearchPresent ? queryParams.search.trim() : null
        }).unwrap();
      } catch (err) {
        setIsError(true);
      }
    }
    page.current = page.current + 1;
  }, [currentTeam?.id, page.current, queryParams]);

  const resetQuery = () => {
    resetListAndLoadMore();
  };

  return {
    isLoading: isLoading,
    isFetching: receiptsState?.isFetching,
    totalListCount,
    list,
    isError,
    loadMore,
    resetQuery,
    hasMore,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: receiptsState.isSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType
  };
};
